import { useCallback } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { TutorialPages } from '../../../../common/src/model.ts';
import { getTutorialPath, Pages } from '../../../../common/src/pages.ts';
import { useUser } from '../../data/use_user.tsx';
import { CheckBox } from '../../util/check_box.tsx';
import { DialogPage } from '../settings/dialog_page.tsx';
import * as family_worship from './family_worship.tsx';
import * as group_harmony from './group_harmony.tsx';
import { useCompleteTutorial } from './use_complete_tutorial.tsx';
import * as your_harmony_part from './your_harmony_part.tsx';

export function Tutorials() {
  const page = useTutorialPage();
  const { user } = useUser();
  const tutorialsCompleted = user?.tutorialsCompleted;

  if (page) {
    return <Outlet />;
  }

  return (
    <DialogPage title="Tutorials">
      <TutorialsList>
        <Tutorial
          title={family_worship.title}
          page={TutorialPages.FamilyWorship}
          tutorialsCompleted={tutorialsCompleted}
        />
        <Tutorial title={your_harmony_part.title} tutorialsCompleted={tutorialsCompleted} />
        <Tutorial title={group_harmony.title} tutorialsCompleted={tutorialsCompleted} />
      </TutorialsList>
    </DialogPage>
  );
}

function Tutorial({
  title,
  page,
  tutorialsCompleted,
}: {
  title: string;
  page?: TutorialPages;
  tutorialsCompleted?: TutorialPages[];
}) {
  const completeTutorial = useCompleteTutorial();
  const isCompleted = page !== undefined && (tutorialsCompleted?.includes(page) ?? false);
  const onCheckBoxClick = useCallback(async () => {
    if (!page) {
      return;
    }
    await completeTutorial(page);
  }, [completeTutorial, page]);
  return (
    <TutorialWrapper>
      <CheckBox onClick={onCheckBoxClick} isChecked={isCompleted} />
      <TutorialTitle>{title}</TutorialTitle>
      {page ? (
        <StyledLink to={getTutorialPath(page)}>
          <GetStartedWrapper>Get Started</GetStartedWrapper>
        </StyledLink>
      ) : (
        <ComingSoon />
      )}
    </TutorialWrapper>
  );
}

function ComingSoon() {
  return <ComingSoonWrapper>Coming Soon!</ComingSoonWrapper>;
}

// white-space: normal restores title wrapping inside DialogPage's nowrap InnerPageContent
const TutorialsList = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  white-space: normal;
`;

const TutorialWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 15px;
  cursor: pointer;
`;

const TutorialTitle = styled.div`
  flex: 1;
`;

// How to learn your harmony part
// How to teach a group to sing in harmony

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const GetStartedWrapper = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.8rem;
  width: 8ch;
  text-align: center;
  padding: 3px;
  border-radius: 10px;
  color: var(--color-text-white);
  background-color: var(--color-primary-button);
`;

const ComingSoonWrapper = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.8rem;
  width: 8ch;
  text-align: center;
  padding: 3px;
  border-radius: 10px;
  border: 2px solid var(--color-text);
`;

function useTutorialPage(): TutorialPages | undefined {
  const { pathname } = useLocation();
  const prefix = `/${Pages.Tutorials}/`;
  if (!pathname.startsWith(prefix)) {
    return;
  }
  const tutorialPage = pathname.slice(prefix.length);
  if (tutorialPage && Object.values(TutorialPages).includes(tutorialPage as TutorialPages)) {
    return tutorialPage as TutorialPages;
  }
}
